import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { UserPlus, Copy, Loader2, Check } from "lucide-react";
import { CO_PRODUCER_MIN_PASSWORD_LENGTH, buildCoProducerLink } from "@/lib/coProducerAccess";

interface CoProducerManagerProps {
  /** The briefing (production) id co-producers are granted access to. */
  productionId: string;
}

interface CoProducerRow {
  id: string;
  label: string;
  access_token: string;
  created_at: string;
  last_login_at: string | null;
}

const formatWhen = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : "Never");

/**
 * Owner-only panel to manage co-producers of a single production: list them,
 * add one (set a label + password → get a secret link to share), and revoke.
 * Co-producers themselves never see this component.
 */
export default function CoProducerManager({ productionId }: CoProducerManagerProps) {
  const queryClient = useQueryClient();
  const queryKey = ["co-producers", productionId];

  const { data: coProducers = [], isLoading } = useQuery({
    queryKey,
    queryFn: async (): Promise<CoProducerRow[]> => {
      const { data, error } = await supabase
        .from("production_co_producers")
        .select("id, label, access_token, created_at, last_login_at")
        .eq("briefing_id", productionId)
        .is("revoked_at", null)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as CoProducerRow[];
    },
  });

  // ── Add dialog state ────────────────────────────────────────────────
  const [addOpen, setAddOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [password, setPassword] = useState("");
  const [createdLink, setCreatedLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const resetAddForm = () => {
    setLabel("");
    setPassword("");
    setCreatedLink(null);
    setCopied(false);
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("create-co-producer", {
        body: { briefing_id: productionId, label: label.trim(), password },
      });
      if (error) throw new Error(error.message || "Failed to add co-producer");
      if (!data?.success || !data?.link)
        throw new Error(data?.error || "Failed to add co-producer");
      return data.link as string;
    },
    onSuccess: (link) => {
      setCreatedLink(link);
      queryClient.invalidateQueries({ queryKey });
      toast.success("Co-producer added");
    },
    onError: (e: unknown) => {
      toast.error(e instanceof Error ? e.message : "Failed to add co-producer");
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.functions.invoke("revoke-co-producer", {
        body: { co_producer_id: id },
      });
      if (error) throw new Error(error.message || "Failed to revoke");
      if (!data?.success) throw new Error(data?.error || "Failed to revoke");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success("Co-producer access revoked");
    },
    onError: (e: unknown) => {
      toast.error(e instanceof Error ? e.message : "Failed to revoke");
    },
  });

  const [revokeTarget, setRevokeTarget] = useState<CoProducerRow | null>(null);

  // ── Manage (show link + reset password) dialog ──────────────────────
  const [manageTarget, setManageTarget] = useState<CoProducerRow | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [manageCopied, setManageCopied] = useState(false);

  const openManage = (cp: CoProducerRow) => {
    setNewPassword("");
    setManageCopied(false);
    setManageTarget(cp);
  };

  const resetPasswordMutation = useMutation({
    mutationFn: async () => {
      if (!manageTarget) return;
      const { data, error } = await supabase.functions.invoke("reset-co-producer-password", {
        body: { co_producer_id: manageTarget.id, password: newPassword },
      });
      if (error) throw new Error(error.message || "Failed to reset password");
      if (!data?.success) throw new Error(data?.error || "Failed to reset password");
    },
    onSuccess: () => {
      setNewPassword("");
      toast.success("New password set — share it with the co-producer");
    },
    onError: (e: unknown) => {
      toast.error(e instanceof Error ? e.message : "Failed to reset password");
    },
  });

  const canSubmit = label.trim().length > 0 && password.length >= CO_PRODUCER_MIN_PASSWORD_LENGTH;

  const copyLink = async (text: string, onCopied: () => void) => {
    try {
      await navigator.clipboard.writeText(text);
      onCopied();
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy — select and copy the link manually");
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-base">Co-producers</CardTitle>
          <CardDescription>
            Give someone the same editing access to this production. They sign in with a secret link
            and password — and only see this production.
          </CardDescription>
        </div>
        <Button
          size="sm"
          onClick={() => {
            resetAddForm();
            setAddOpen(true);
          }}
        >
          <UserPlus className="mr-2 h-4 w-4" />
          Add co-producer
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="py-4 text-sm text-muted-foreground">Loading…</div>
        ) : coProducers.length === 0 ? (
          <div className="py-4 text-sm text-muted-foreground">No co-producers yet.</div>
        ) : (
          <ul className="divide-y divide-border">
            {coProducers.map((cp) => (
              <li key={cp.id} className="flex items-center justify-between gap-4 py-3">
                <div>
                  <div className="font-medium">{cp.label}</div>
                  <div className="text-xs text-muted-foreground">
                    Last login: {formatWhen(cp.last_login_at)}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openManage(cp)}>
                    Show link
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setRevokeTarget(cp)}
                  >
                    Revoke
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      {/* Add co-producer dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add co-producer</DialogTitle>
            <DialogDescription>
              Pick a name to recognise them by and a password. You'll get a secret link to share.
            </DialogDescription>
          </DialogHeader>

          {createdLink ? (
            <div className="space-y-3">
              <p className="text-sm">
                Share this link <strong>and</strong> the password you just set with the co-producer.
                Keep them safe — together they give access to this production.
              </p>
              <div className="flex items-center gap-2">
                <Input readOnly value={createdLink} onFocus={(e) => e.currentTarget.select()} />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => createdLink && copyLink(createdLink, () => setCopied(true))}
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <DialogFooter>
                <Button onClick={() => setAddOpen(false)}>Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (canSubmit) createMutation.mutate();
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="cp-label">Name / label</Label>
                <Input
                  id="cp-label"
                  placeholder="e.g. Jan — stage manager"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  maxLength={80}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cp-password">Password</Label>
                <PasswordInput
                  id="cp-password"
                  placeholder={`At least ${CO_PRODUCER_MIN_PASSWORD_LENGTH} characters`}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={CO_PRODUCER_MIN_PASSWORD_LENGTH}
                  required
                />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={!canSubmit || createMutation.isPending}>
                  {createMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating…
                    </>
                  ) : (
                    "Create link"
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Manage: re-show link + reset password */}
      <Dialog open={!!manageTarget} onOpenChange={(open) => !open && setManageTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{manageTarget?.label}</DialogTitle>
            <DialogDescription>
              Re-share the access link, or set a new password if it was lost.
            </DialogDescription>
          </DialogHeader>

          {manageTarget && (
            <div className="space-y-5">
              <div className="space-y-2">
                <Label>Access link</Label>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={buildCoProducerLink(manageTarget.access_token)}
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() =>
                      copyLink(buildCoProducerLink(manageTarget.access_token), () =>
                        setManageCopied(true),
                      )
                    }
                  >
                    {manageCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  The password can't be shown again. If it was lost, set a new one below.
                </p>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (newPassword.length >= CO_PRODUCER_MIN_PASSWORD_LENGTH)
                    resetPasswordMutation.mutate();
                }}
                className="space-y-2"
              >
                <Label htmlFor="cp-new-password">New password</Label>
                <div className="flex items-center gap-2">
                  <PasswordInput
                    id="cp-new-password"
                    placeholder={`At least ${CO_PRODUCER_MIN_PASSWORD_LENGTH} characters`}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    minLength={CO_PRODUCER_MIN_PASSWORD_LENGTH}
                  />
                  <Button
                    type="submit"
                    variant="outline"
                    disabled={
                      newPassword.length < CO_PRODUCER_MIN_PASSWORD_LENGTH ||
                      resetPasswordMutation.isPending
                    }
                  >
                    {resetPasswordMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Set"
                    )}
                  </Button>
                </div>
              </form>

              <DialogFooter>
                <Button onClick={() => setManageTarget(null)}>Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Revoke confirmation */}
      <AlertDialog open={!!revokeTarget} onOpenChange={(open) => !open && setRevokeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke access?</AlertDialogTitle>
            <AlertDialogDescription>
              {revokeTarget?.label} will immediately lose access to this production. Their link and
              password stop working. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (revokeTarget) revokeMutation.mutate(revokeTarget.id);
                setRevokeTarget(null);
              }}
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
